import { pickBy } from 'lodash';

import { isNonNullable, type NullableFields } from '@pressreader/types';

import { ISize } from '../types/size';

function getClosestSize(original: ISize, possible: (Partial<ISize> | NullableFields<ISize>)[], avoidSmallerSizes = true): Partial<ISize> | ISize {
    let closest: Partial<ISize> | null = null;
    let closestDelta: number = Number.MAX_VALUE;

    for (const size of possible) {
        if (avoidSmallerSizes) {
            if (size.width && original.width > size.width) {
                continue;
            }

            if (size.height && original.height > size.height) {
                continue;
            }
        }

        // possible size could have width, height, or both. trying to find best option among such values.
        const dw = size.width ? Math.abs(size.width - original.width) / original.width : 1;
        const dh = size.height ? Math.abs(size.height - original.height) / original.height : 1;
        const d = dw * dh;

        if (d < closestDelta) {
            closest = pickBy(size, isNonNullable);
            closestDelta = d;
        }
    }

    return pickBy(closest || possible[possible.length - 1], isNonNullable);
}

/**
 * Finds closest size from provided array of sizes.
 */
function getClosestSizeByDirection(original: ISize, possible: ISize[], direction: 'up' | 'down' = 'down'): ISize {
    if (original === null) {
        return possible[0];
    }

    let index = -1;
    let delta = Number.MAX_VALUE;

    for (let i = 0; i < possible.length; i++) {
        const size = possible[i];

        // check only smallest possible sizes
        if (original.width > size.width || original.height > size.height) {
            continue;
        }

        const dw = Math.abs(size.width - original.width) / original.width;
        const dh = Math.abs(size.height - original.height) / original.height;
        const d = dw * dh;

        if (d < delta) {
            index = i;
            delta = d;
        }
    }

    if (index === -1) {
        index = possible.length - 1;
    } else if (index > 0) {
        index = direction === 'up' ? index : index - 1;
    }

    return possible[index];
}

/** @param round A method from `Math` to round the values if needed. */
function scaleSize(size: ISize, scale: number, round?: 'round' | 'floor' | 'ceil'): ISize {
    let width = size.width * scale;
    let height = size.height * scale;

    if (round) {
        width = Math[round](width);
        height = Math[round](height);
    }

    return { width, height };
}

export { getClosestSize, getClosestSizeByDirection, scaleSize };
